'use client';

import { QueryClient, QueryClientProvider, QueryObserverOptions } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { queryClientAtom } from 'jotai-tanstack-query';
import { useHydrateAtoms } from 'jotai/utils';
import { PropsWithChildren, useState } from 'react';

import { checkMaintenanceError } from '@flipster/utils/axios';
import { isServer } from '@flipster/utils/env-helper';

import { authBunja } from '../shared/auth/state';

interface BaseProps extends PropsWithChildren {
  options?: Partial<QueryObserverOptions>;
}

export const ReactQueryProvider = ({ options, children }: BaseProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            ...options,
          },
        },
      }),
  );
  useHydrateAtoms([[queryClientAtom, queryClient]]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

interface Props extends PropsWithChildren {
  isBuild?: boolean;
}

export const WebviewReactQueryProvider = ({ isBuild, children }: Props) => {
  return (
    <ReactQueryProvider
      options={{
        refetchOnWindowFocus: isBuild,
        staleTime: isBuild ? 30 * 1000 : undefined,
      }}
    >
      {children}
    </ReactQueryProvider>
  );
};

export const WebReactQueryProvider = ({ isBuild, children }: Props) => {
  const { checkSessionError } = useBunja(authBunja);

  return (
    <ReactQueryProvider
      options={{
        refetchOnWindowFocus: isBuild,
        staleTime: isBuild ? 30 * 1000 : undefined,
        throwOnError(err) {
          // 503은 무조건 해주기 503의 message 가 없어야 점검중
          if (!isServer && window.location.pathname.includes('/maintenance')) return false;
          if (checkMaintenanceError(err)) return true;
          checkSessionError(err);
          return false;
        },
      }}
    >
      {children}
    </ReactQueryProvider>
  );
};
